import { DateTime } from "luxon";
import { PublicTransportImportType } from "../constant/PublicTransportImportType";
import { ComparisonReport } from "../types/LayerAdmin/ComparisonReport";
import { PublicTransportSource } from "../types/LayerAdmin/PublicTransportSource";
import { SnapshotWindow } from "../types/LayerAdmin/SnapshotWindow";
import { Pagination } from "../types/PaginiationResult";
import LayerAdminService from "./LayerAdminService";
import { PublicTransportImport } from "../types/LayerAdmin/PublicTransportImport";
import { PublicTransportDownloadSummary } from "../types/LayerAdmin/PublicTransportDownloadSummary";

export default class PublicTransportService extends LayerAdminService {
  private _lbBaseUrl = process.env.REACT_APP_LB_URL;

  public async DeletePublicTransportSource(sourceId: number): Promise<any> {
    return this._client
      .delete<any>(`${this._endpoint}/Basemap:PublicTransport/${sourceId}`, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        return response.data;
      });
  }

  public async SetSnapshotWindow(
    windowStartDate: DateTime,
    windowEndDate: DateTime
  ): Promise<any> {
    const obj = {
      windowStartDate: windowStartDate,
      windowEndDate: windowEndDate,
    };

    const queryString = this.objectToQueryString(obj);

    return this._client
      .patch<any>(
        `${this._endpoint}/Basemap:PublicTransport/SnapshotWindow${queryString}`
      )
      .then((response) => {
        return response.data;
      });
  }

  public async GetSnapshotWindow(): Promise<SnapshotWindow[]> {
    return this._client
      .get<any>(`${this._endpoint}/Basemap:PublicTransport/SnapshotWindow`)
      .then((response) => {
        return response.data;
      });
  }

  public async StartTransitLandImport(): Promise<any> {
    return this._client
      .post<any>(`${this._endpoint}/Basemap:PublicTransport/TransitLand`)
      .then((response) => {
        return response.data;
      });
  }

  public async StartUsTransitImport(): Promise<any> {
    return this._client
      .post<any>(`${this._endpoint}/Basemap:PublicTransport/UsTransit`)
      .then((response) => {
        return response.data;
      });
  }

  public async StartUsTransitMerger(
    usTransitImportId: number,
    transitLandImportId: number
  ): Promise<any> {
    return this._client
      .post<any>(`${this._endpoint}/Basemap:PublicTransport/UsTransit/Merge`, {
        usTransitImportId,
        transitLandImportId,
      })
      .then((response) => {
        return response.data;
      });
  }

  public async StartTravelineImport(): Promise<any> {
    return this._client
      .post<any>(`${this._endpoint}/Basemap:PublicTransport/Traveline`)
      .then((response) => {
        return response.data;
      });
  }

  public async StartAtocImport(file: File): Promise<any> {
    const formData = new FormData();
    formData.append("atocArchive", file);

    return this._client
      .post<any>(`${this._endpoint}/Basemap:PublicTransport/Atoc`, formData, {
        timeout: 0,
        baseURL: this._lbBaseUrl,
      })
      .then((response) => {
        return response.data;
      });
  }

  public async StartNcsdImport(file: File): Promise<any> {
    const formData = new FormData();
    formData.append("ncsdArchive", file);

    return this._client
      .post<any>(`${this._endpoint}/Basemap:PublicTransport/Ncsd`, formData, {
        timeout: 0,
        baseURL: this._lbBaseUrl,
      })
      .then((response) => {
        return response.data;
      });
  }

  public async StartGtfsImport(
    file: File,
    quarter: string,
    description: string
  ): Promise<any> {
    const formData = new FormData();
    formData.append("gtfsArchive", file);
    formData.append("importQuarter", quarter);
    formData.append("description", description);

    return this._client
      .post<any>(`${this._endpoint}/Basemap:PublicTransport/Gtfs`, formData, {
        timeout: 0,
        baseURL: this._lbBaseUrl,
      })
      .then((response) => {
        return response.data;
      });
  }

  public async StartTranslinkImport(
    busArchive: File,
    railArchive: File,
    metroArchive: File
  ): Promise<any> {
    const formData = new FormData();
    formData.append("busArchive", busArchive);
    formData.append("railArchive", railArchive);
    formData.append("metroArchive", metroArchive);

    return this._client
      .post<any>(
        `${this._endpoint}/Basemap:PublicTransport/Translink`,
        formData,
        {
          timeout: 0,
          baseURL: this._lbBaseUrl,
        }
      )
      .then((response) => {
        return response.data;
      });
  }

  public ComparePublicTransportImporters(
    type: PublicTransportImportType
  ): Promise<ComparisonReport> {
    return this._client
      .get<any>(`${this._endpoint}/Basemap:PublicTransport/Comparison/${type}`)
      .then((response) => {
        return response.data;
      });
  }

  public async GetPublicTransportFeeds(
    quarter: string,
    page?: number,
    pageSize?: number,
    orderBy?: string,
    filter?: string
  ): Promise<Pagination<PublicTransportSource>> {
    const request = { quarter, page, pageSize, orderBy, filter };

    const queryString = this.objectToQueryString(request);

    return this._client
      .get<any>(`${this._endpoint}/Basemap:PublicTransport/Feeds${queryString}`)
      .then((response) => {
        return response.data;
      });
  }

  public async GetCurrentQuarterInProgressDownloads(): Promise<any> {
    return this._client
      .get<any>(
        `${this._endpoint}/Basemap:PublicTransport/Downloads/InProgress`
      )
      .then((response) => {
        return response.data;
      });
  }

  public async GetDownloads(): Promise<any> {
    return this._client
      .get<any>(`${this._endpoint}/Basemap:PublicTransport/Downloads`)
      .then((response) => {
        return response.data;
      });
  }

  public async GetPublicTransportDownloadSummary(
    downloadId: number
  ): Promise<PublicTransportDownloadSummary> {
    return this._client
      .get<any>(
        `${this._endpoint}/Basemap:PublicTransport/Downloads/${downloadId}/summary`
      )
      .then((response) => {
        return response.data;
      });
  }

  public async GetPublicTansportDownloadSources(
    downloadId: number
  ): Promise<any> {
    return this._client
      .get<any>(
        `${this._endpoint}/Basemap:PublicTransport/DownloadSources/${downloadId}`
      )
      .then((response) => {
        return response.data;
      });
  }

  public async IgnoreDownloadSource(downloadSourceId: number): Promise<any> {
    return this._client
      .post<any>(
        `${this._endpoint}/Basemap:PublicTransport/DownloadSource/${downloadSourceId}/Ignore`
      )
      .then((response) => {
        return response.data;
      });
  }

  public async PatchDownloadSource(
    downloadSourceId: number,
    file: File
  ): Promise<any> {
    const formData = new FormData();
    formData.append("patchFile", file);

    return this._client
      .post<any>(
        `${this._endpoint}/Basemap:PublicTransport/DownloadSource/${downloadSourceId}/Patch`,
        formData,
        {
          timeout: 0,
          baseURL: this._lbBaseUrl,
        }
      )
      .then((response) => {
        return response.data;
      });
  }

  public async DownloadSource(downloadSourceId: number): Promise<any> {
    return this._client
      .get(
        `${this._endpoint}/Basemap:PublicTransport/DownloadSources/${downloadSourceId}/Download`,
        {
          responseType: "blob",
          timeout: 0,
          baseURL: this._lbBaseUrl,
        }
      )
      .then((response) => {
        const contentDisposition = response.headers["content-disposition"];
        let filename = "download.file";

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(
            /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
          );
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, "");
          }
        }

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();

        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      });
  }

  public async GetImports(quarter: string): Promise<PublicTransportImport[]> {
    return this._client
      .get<any>(
        `${this._endpoint}/Basemap:PublicTransport/PtImports?quarter=${quarter}`
      )
      .then((response) => {
        return response.data;
      });
  }

  public async PublishImport(importId: number): Promise<any> {
    return this._client
      .post<any>(
        `${this._endpoint}/Basemap:PublicTransport/PtImports/${importId}/Publish`
      )
      .then((response) => {
        return response.data;
      });
  }

  public async UnpublishImport(importId: number): Promise<any> {
    return this._client
      .post<any>(
        `${this._endpoint}/Basemap:PublicTransport/PtImports/${importId}/Unpublish`
      )
      .then((response) => {
        return response.data;
      });
  }

  public async RetryDownloadSourceDownloads(
    downloadSourceId: number,
    failureReason: string | null
  ): Promise<any> {
    return this._client
      .post<any>(`${this._endpoint}/Basemap:PublicTransport/Downloads/Retry`, {
        failureReason: failureReason,
        publicTransportDownloadId: downloadSourceId,
      })
      .then((response) => {
        return response.data;
      });
  }

  public async StartBoundaryVectorTileImport(): Promise<void> {
    return this._client
      .post(`${this._endpoint}/Basemap:PublicTransport/BoundaryVectorTiles`)
      .then((response) => {
        return response.data;
      });
  }

  private objectToQueryString = (obj: any) => {
    const parts = [];

    for (const key in obj) {
      if (obj.hasOwnProperty(key) && obj[key] !== undefined) {
        let value = obj[key];

        if (value instanceof DateTime) {
          // Convert Date objects to ISO strings
          value = value.toUTC().toISO({ includeOffset: false });
        }

        parts.push(encodeURIComponent(key) + "=" + encodeURIComponent(value));
      }
    }

    return "?" + parts.join("&");
  };
}
